import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import type { RootState } from 'state/store'

import {
  ApiAssetPriceUserOverride,
  ApiAssetPriceWithHistory,
  ApiAssetPriceWithOverride,
  ApiAssetWithConstituents,
  ApiDistributionAlert,
  ApiDistributionData,
  ApiFxData,
  ApiMutualFundAssetRead,
  ApiOverrideInfo,
  ApiOverridenAssetPrice,
  ApiReinvestmentConfirmation,
  ApiSegregatedPortfolioAssetRead,
  AssetConcentrationReportRead as AssetConcentrationReport,
  CustomAssetScenarioResult,
  MaxDrawdownReport,
  ScenarioPoint,
  SingleAssetDrawdownScenarioResult,
  SingleAssetLadderScenarioResult
} from '@/api'
import api from '@/api/_legacy'
import { defaultQueryParams, QueryParams } from '@/api/_legacy/types'
import { TablePaginationParams } from '@/components/core-v2/molecules/table-pagination'
import { ExDateDistribution, ExDateReinvestment } from '@/components/core-v2/organisms'
import { UserDisplayInfo } from '@/types/user'

import { Errors, extractErrorMessagesFromServerResponse } from '../../tradebuilder-v2/util'

const TENANT_ID = 'absa'

export type GapRiskSearchFilter = {
  searchTerm: string
  showAll: boolean
  showRequiringAction: boolean
}

export type ExDateReinvestmentMapping = {
  confirmationId: string
  exDate: string
}

export type GapRiskState = {
  areOverridesAllowed?: boolean
  assetBeingInspected: ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead
  assetConcentration: AssetConcentrationReport
  assetConstituents: ApiAssetWithConstituents[]
  assetPriceAlerts: ApiDistributionAlert[]
  assetPrices: ApiAssetPriceWithHistory[]
  currentPageNumber: number
  customAssetScenario: CustomAssetScenarioResult
  customFundManagerScenario: CustomAssetScenarioResult
  distributions: ApiDistributionData[]
  errors?: any
  exDateNotification?: string
  exDateSaveErrors?: Errors
  filter: GapRiskSearchFilter
  fundManagerDrawdownResult: SingleAssetDrawdownScenarioResult
  fundManagerLadder?: Record<string, ScenarioPoint[]>
  fxConversionRates: ApiFxData[]
  isCheckingPriceOverrideDetail: boolean
  isInspectingAsset: boolean
  isLoading: boolean
  isSubmittingPriceOverride: boolean
  maxDrawdownReport: MaxDrawdownReport
  numTotalTrades: number
  priceOverride: number
  priceOverrideErrors: Errors
  priceOverrideInfo?: ApiOverrideInfo
  query: QueryParams
  reinvestments: ApiReinvestmentConfirmation[]
  scenarioLadder?: Record<string, ScenarioPoint[]>
  selectedAssetId: string
  selectedAssetPrice: ApiAssetPriceWithHistory
  singleAssetDrawdownResult: SingleAssetDrawdownScenarioResult
  unmappedReinvestments: ApiReinvestmentConfirmation[]
  updatedDistribution: ExDateDistribution
  updatedReinvestment: ExDateReinvestment
  userInfo: UserDisplayInfo
}

const initialState: GapRiskState = {
  numTotalTrades: 0,
  areOverridesAllowed: undefined,
  assetConstituents: undefined,
  assetConcentration: undefined,
  selectedAssetId: undefined,
  selectedAssetPrice: undefined,
  customAssetScenario: undefined,
  customFundManagerScenario: undefined,
  currentPageNumber: 1,
  priceOverride: undefined,
  priceOverrideInfo: undefined,
  singleAssetDrawdownResult: undefined,
  fundManagerLadder: undefined,
  fundManagerDrawdownResult: undefined,
  assetPrices: [],
  assetPriceAlerts: [],
  distributions: [],
  reinvestments: [],
  unmappedReinvestments: [],
  updatedDistribution: undefined,
  updatedReinvestment: undefined,
  isInspectingAsset: false,
  isCheckingPriceOverrideDetail: false,
  isSubmittingPriceOverride: false,
  assetBeingInspected: undefined,
  query: { ...defaultQueryParams, PageSize: 20 },
  filter: { searchTerm: '', showAll: true, showRequiringAction: false },
  isLoading: false,
  userInfo: undefined,
  maxDrawdownReport: undefined,
  priceOverrideErrors: undefined,
  fxConversionRates: undefined
}

const createOverriddenPrice = (
  overriddenAssetPrice: ApiOverridenAssetPrice
): ApiAssetPriceWithOverride => ({
  isManuallyOverridden: true,
  price: overriddenAssetPrice.newPrice,
  receivedDateTime: overriddenAssetPrice.overrideDateTime,
  valueDate: overriddenAssetPrice.valueDate,
  source: overriddenAssetPrice.overrideAuthor,
  displayName:
    overriddenAssetPrice.overrideAuthorDisplayName ||
    overriddenAssetPrice.overrideAuthor ||
    'User Override'
})

export const gapRiskSlice = createSlice({
  name: 'gapRisk',
  initialState,
  reducers: {
    init: (state, action: PayloadAction<UserDisplayInfo>) => {
      state.isLoading = true
      state.userInfo = action.payload
      state.exDateNotification = undefined
    },
    fetchGapRiskSuccess: (state, action: PayloadAction<ApiAssetPriceWithHistory[]>) => {
      state.assetPrices = action.payload
      state.isLoading = false
    },
    fetchGapRiskFailure: (state, action: PayloadAction<any>) => {
      state.isLoading = false
      state.errors = action.payload
    },
    fetchGapRiskAlertsSuccess: (state, action: PayloadAction<ApiDistributionAlert[]>) => {
      state.assetPriceAlerts = action.payload
    },
    fetchGapRiskAlertsFailure: (state, action: PayloadAction<any>) => {
      state.assetPriceAlerts = []
      state.errors = action.payload
    },
    fetchScenarioLadderSuccess: (state, action: PayloadAction<SingleAssetLadderScenarioResult>) => {
      state.scenarioLadder = action.payload.values
    },
    fetchScenarioLadderFailure: (state, action: PayloadAction<any>) => {
      state.scenarioLadder = undefined
      state.errors = action.payload
    },
    fetchFundManagerLadderSuccess: (
      state,
      action: PayloadAction<SingleAssetLadderScenarioResult>
    ) => {
      state.fundManagerLadder = action.payload.values
    },
    fetchFundManagerLadderFailure: (state, action: PayloadAction<any>) => {
      state.fundManagerLadder = undefined
      state.errors = action.payload
    },
    fetchSingleAssetDrawdownSuccess: (
      state,
      action: PayloadAction<SingleAssetDrawdownScenarioResult>
    ) => {
      state.singleAssetDrawdownResult = action.payload
    },
    fetchSingleAssetDrawdownFailure: (state, action: PayloadAction<any>) => {
      state.singleAssetDrawdownResult = undefined
      state.errors = action.payload
    },
    fetchFundManagerDrawdownSuccess: (
      state,
      action: PayloadAction<SingleAssetDrawdownScenarioResult>
    ) => {
      state.fundManagerDrawdownResult = action.payload
    },
    fetchFundManagerDrawdownFailure: (state, action: PayloadAction<any>) => {
      state.fundManagerDrawdownResult = undefined
      state.errors = action.payload
    },
    fetchMaxDrawdownSuccess: (state, action: PayloadAction<MaxDrawdownReport>) => {
      state.maxDrawdownReport = action.payload
    },
    fetchMaxDrawdownFailure: (state, action: PayloadAction<any>) => {
      state.maxDrawdownReport = undefined
      state.errors = action.payload
    },
    fetchAssetConcentrationSuccess: (state, action: PayloadAction<AssetConcentrationReport>) => {
      state.assetConcentration = action.payload
    },
    fetchAssetConcentrationFailure: (state, action: PayloadAction<any>) => {
      state.assetConcentration = undefined
      state.errors = action.payload
    },
    fetchAssetLookthroughSuccess: (state, action: PayloadAction<ApiAssetWithConstituents[]>) => {
      state.assetConstituents = action.payload
    },
    fetchAssetLookthroughFailure: (state, action: PayloadAction<any>) => {
      state.assetConstituents = undefined
      state.errors = action.payload
    },
    fetchCustomAssetBumpsSuccess: (state, action: PayloadAction<CustomAssetScenarioResult>) => {
      state.customAssetScenario = action.payload
    },
    fetchCustomAssetBumpsFailure: (state, action: PayloadAction<any>) => {
      state.customAssetScenario = undefined
      state.errors = action.payload
    },
    fetchCustomFundManagerBumpsSuccess: (
      state,
      action: PayloadAction<CustomAssetScenarioResult>
    ) => {
      state.customFundManagerScenario = action.payload
    },
    fetchCustomFundManagerBumpsFailure: (state, action: PayloadAction<any>) => {
      state.customFundManagerScenario = undefined
      state.errors = action.payload
    },
    fetchFxRateSuccess: (state, action: PayloadAction<ApiFxData[]>) => {
      state.fxConversionRates = action.payload
    },
    fetchFxRateFailure: (state, action: PayloadAction<any>) => {
      state.fxConversionRates = undefined
      state.errors = action.payload
    },

    setServerFilter: (state, action: PayloadAction<GapRiskSearchFilter>) => {
      state.isLoading = true
      state.assetPrices = []
      state.query.PageNumber = 1

      const filter = action.payload
      if (filter.searchTerm) {
        state.query.WildcardString = filter.searchTerm
      } else {
        state.query.WildcardString = undefined
      }

      state.filter = filter
    },
    selectedAssetPrice: (state, action: PayloadAction<ApiAssetPriceWithHistory>) => {
      state.selectedAssetPrice = action.payload
      state.areOverridesAllowed = undefined
      state.isCheckingPriceOverrideDetail = true
      state.priceOverrideInfo = undefined
      state.priceOverrideErrors = undefined
    },
    stopPriceOverride: state => {
      state.selectedAssetPrice = undefined
      state.areOverridesAllowed = undefined
      state.isCheckingPriceOverrideDetail = false
      state.priceOverrideInfo = undefined
      state.priceOverrideErrors = undefined
    },
    refreshPriceOverrideDetail: state => {
      state.isCheckingPriceOverrideDetail = true
    },
    onPriceOverride: (state, action: PayloadAction<number>) => {
      state.priceOverride = action.payload
      state.isSubmittingPriceOverride = true
    },
    inspectAsset: (state, action: PayloadAction<string>) => {
      state.selectedAssetId = action.payload
    },
    inspectAssetSuccess: (
      state,
      action: PayloadAction<ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead>
    ) => {
      state.isInspectingAsset = true
      state.assetBeingInspected = action.payload
    },
    priceOverrideDetailSuccess: (state, action: PayloadAction<ApiOverrideInfo>) => {
      state.areOverridesAllowed = action.payload.areOverridesAllowed
      state.isCheckingPriceOverrideDetail = false
      state.priceOverrideInfo = action.payload
      state.priceOverrideErrors = undefined
    },
    priceOverrideDetailFailure: (state, action: PayloadAction<Errors>) => {
      state.isCheckingPriceOverrideDetail = false
      state.priceOverrideInfo = undefined
      state.priceOverrideErrors = action.payload
    },
    setPriceOverrideSuccess: (state, action: PayloadAction<ApiOverridenAssetPrice>) => {
      state.priceOverrideErrors = undefined
      state.isSubmittingPriceOverride = false

      if (!action.payload.isinCode) {
        return
      }

      const overriddenPrice = createOverriddenPrice(action.payload)

      if (state.selectedAssetPrice?.isinCode === action.payload.isinCode) {
        const existingPrices = state.selectedAssetPrice.allPrices ?? []
        state.selectedAssetPrice.currentPrice = overriddenPrice
        state.selectedAssetPrice.allPrices = [overriddenPrice, ...existingPrices].sort(
          (a, b) =>
            new Date(b.receivedDateTime ?? 0).getTime() -
            new Date(a.receivedDateTime ?? 0).getTime()
        )
      }

      state.assetPrices = state.assetPrices.map(assetPrice =>
        assetPrice.isinCode === action.payload.isinCode
          ? {
              ...assetPrice,
              currentPrice: overriddenPrice,
              allPrices: [overriddenPrice, ...(assetPrice.allPrices ?? [])].sort(
                (a, b) =>
                  new Date(b.receivedDateTime ?? 0).getTime() -
                  new Date(a.receivedDateTime ?? 0).getTime()
              )
            }
          : assetPrice
      )
    },
    setPriceOverrideFailure: (state, action: PayloadAction<Errors>) => {
      state.priceOverrideErrors = action.payload
      state.isSubmittingPriceOverride = false
    },
    stopInspectingAsset: state => {
      state.selectedAssetId = undefined
      state.isInspectingAsset = false
      state.assetBeingInspected = undefined
    },
    setPage: (state, action: PayloadAction<TablePaginationParams>) => {
      state.query.PageNumber = action.payload.pageNumber
      state.query.PageSize = action.payload.pageSize
      state.isLoading = true
    },
    clearFilters: state => {
      state.query.Status = ''
      state.query.WildcardString = ''
    }
  }
})

export const { actions } = gapRiskSlice
export type GapRiskActions = typeof actions

function* fetchGapRiskSaga() {
  try {
    const response = yield call(api.tenant.latestAssetspricesDetail, TENANT_ID)
    yield put({
      type: 'gapRisk/fetchGapRiskSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'gapRisk/fetchGapRiskFailure', payload: error })
  }
}

function* fetchGapRiskAlertsSaga() {
  try {
    const response = yield call(api.tenant.assetswithalertsDetail, TENANT_ID)
    yield put({
      type: 'gapRisk/fetchGapRiskAlertsSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'gapRisk/fetchGapRiskAlertsFailure', payload: error })
  }
}

function* fetchScenarioLadderSaga() {
  try {
    const response = yield call(api.tenant.latestSingleassetladderDetail, TENANT_ID)
    yield put({
      type: 'gapRisk/fetchScenarioLadderSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'gapRisk/fetchScenarioLadderFailure', payload: error })
  }
}

function* fetchFundManagerLadderSaga() {
  try {
    const response = yield call(api.tenant.latestFundmanagerladderDetail, TENANT_ID)
    yield put({
      type: 'gapRisk/fetchFundManagerLadderSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'gapRisk/fetchFundManagerLadderFailure', payload: error })
  }
}

function* fetchSingleAssetDrawdownSaga() {
  try {
    const response = yield call(api.tenant.latestSingleassetdrawdownDetail, TENANT_ID)
    yield put({
      type: 'gapRisk/fetchSingleAssetDrawdownSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'gapRisk/fetchSingleAssetDrawdownFailure', payload: error })
  }
}

function* fetchFundManagerDrawdownSaga() {
  try {
    const response = yield call(api.tenant.latestFundmanagerdrawdownDetail, TENANT_ID)
    yield put({
      type: 'gapRisk/fetchFundManagerDrawdownSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'gapRisk/fetchFundManagerDrawdownFailure', payload: error })
  }
}

function* fetchCustomAssetBumpsSaga() {
  try {
    const response = yield call(api.tenant.latestCustomassetDetail, TENANT_ID)
    yield put({
      type: 'gapRisk/fetchCustomAssetBumpsSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'gapRisk/fetchCustomAssetBumpsFailure', payload: error })
  }
}

function* fetchCustomFundManagerBumpsSaga() {
  try {
    const response = yield call(api.tenant.latestCustomfundmanagerDetail, TENANT_ID)
    yield put({
      type: 'gapRisk/fetchCustomFundManagerBumpsSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'gapRisk/fetchCustomFundManagerBumpsFailure', payload: error })
  }
}

function* fetchInspectAssetSaga() {
  const rootState: RootState = yield select()
  const state = rootState.gapRisk
  try {
    const response = yield call(api.tenant.assetDetail2, state.selectedAssetId, TENANT_ID)
    yield put({
      type: 'gapRisk/inspectAssetSuccess',
      payload: response.data
    })
  } catch (error) {
    // ignore
  }
}

function* fetchMaxDrawdownSaga() {
  try {
    const response = yield call(api.tenant.maxdrawdownDetail, 'latest', TENANT_ID)
    yield put({
      type: 'gapRisk/fetchMaxDrawdownSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'gapRisk/fetchMaxDrawdownFailure', payload: error })
  }
}

function* fetchAssetConcentrationSaga() {
  try {
    const response = yield call(api.tenant.assetconcentrationDetail, 'latest', TENANT_ID)
    yield put({
      type: 'gapRisk/fetchAssetConcentrationSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'gapRisk/fetchAssetConcentrationFailure', payload: error })
  }
}

function* fetchAssetLookthroughSaga() {
  try {
    const response = yield call(api.tenant.assetConstituentsDetail, TENANT_ID)
    yield put({
      type: 'gapRisk/fetchAssetLookthroughSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'gapRisk/fetchAssetLookthroughFailure', payload: error })
  }
}

function* fetchFxRateSaga() {
  try {
    const response = yield call(api.tenant.latestMarketdataFxDetail, TENANT_ID)
    yield put({
      type: 'gapRisk/fetchFxRateSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'gapRisk/fetchFxRateFailure', payload: error })
  }
}

function* getPriceOverrideDetailSaga() {
  try {
    const response = yield call(api.tenant.assetpriceoverrideinfoDetail, TENANT_ID)
    yield put({
      type: 'gapRisk/priceOverrideDetailSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({
      type: 'gapRisk/priceOverrideDetailFailure',
      payload: extractErrorMessagesFromServerResponse(error)
    })
  }
}

function* setPriceOverrideSaga() {
  const rootState: RootState = yield select()
  const state = rootState.gapRisk

  try {
    if (state.priceOverride) {
      const request: ApiAssetPriceUserOverride = {
        priceOverride: state.priceOverride,
        isinCode: state.selectedAssetPrice.isinCode
      }
      const response = yield call(api.tenant.assetpriceoverrideCreate, TENANT_ID, request)
      yield put({
        type: 'gapRisk/setPriceOverrideSuccess',
        payload: response.data
      })
    }
  } catch (error) {
    yield put({
      type: 'gapRisk/setPriceOverrideFailure',
      payload: extractErrorMessagesFromServerResponse(error)
    })
  }
}

export function* gapRiskRootSaga() {
  yield all([
    takeLatest('gapRisk/init', fetchGapRiskSaga),
    takeLatest('gapRisk/init', fetchGapRiskAlertsSaga),
    takeLatest('gapRisk/init', fetchScenarioLadderSaga),
    takeLatest('gapRisk/init', fetchMaxDrawdownSaga),
    takeLatest('gapRisk/init', fetchAssetConcentrationSaga),
    takeLatest('gapRisk/init', fetchAssetLookthroughSaga),
    takeLatest('gapRisk/init', fetchAssetLookthroughSaga),
    takeLatest('gapRisk/init', fetchSingleAssetDrawdownSaga),
    takeLatest('gapRisk/init', fetchFundManagerLadderSaga),
    takeLatest('gapRisk/init', fetchFundManagerDrawdownSaga),
    takeLatest('gapRisk/init', fetchCustomAssetBumpsSaga),
    takeLatest('gapRisk/init', fetchCustomFundManagerBumpsSaga),
    takeLatest('gapRisk/init', fetchFxRateSaga),
    takeLatest('gapRisk/inspectAsset', fetchInspectAssetSaga),
    takeLatest('gapRisk/onPriceOverride', setPriceOverrideSaga),
    takeLatest('gapRisk/refreshPriceOverrideDetail', getPriceOverrideDetailSaga),
    takeLatest('gapRisk/search', fetchGapRiskSaga)
  ])
}

export default gapRiskSlice.reducer
